import {
  ADMIN_REFERRAL_DETAILS,
  AdminHeader,
  AdminSpinner,
  Div,
  ListSpinner,
  RestaurantReferralStatus,
  SearchInput,
  Tab,
  TableHeader,
  TabsWrapper,
  handleSetUrlSearchParams,
  useDebouncedValue,
} from '@vgrubs/components';
// Direct import: this one is deliberately outside the barrel.
import { NoDataComponent } from 'components/src/adminComponents/NoDataComponent/NoDataComponent';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { generatePath, useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { REFERRAL_COLUMNS, ReferralRow } from './components/ReferralRow';
import { ReferralSummaryCards } from './components/ReferralSummaryCards';
import { REFERRAL_TABS } from './helpers/referralLabels';
import { useReferralsList } from './hooks/useReferralsList';

/**
 * Restaurant-to-restaurant referrals across the whole program: who brought
 * whom in, and whether the bonus was paid. Admin and support read it. Paying
 * out is the daily job's alone; nothing here triggers it.
 *
 * The status tab and the search live in the URL, so a link lands on the same
 * view - Location Details links here with a restaurant's name as the search.
 */
export const Referrals = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const status =
    (searchParams.get('status') as RestaurantReferralStatus) || null;
  const [search, setSearch] = useState(searchParams.get('query') || '');
  const query = useDebouncedValue(search.trim(), 400);

  const list = useReferralsList({ status, query });

  const [sentinelRef, sentinelInView] = useInView({ rootMargin: '400px' });

  useEffect(() => {
    if (sentinelInView && list.hasNextPage && !list.isFetchingNextPage) {
      list.fetchNextPage();
    }
  }, [sentinelInView, list]);

  const onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    handleSetUrlSearchParams('query', event.target.value, setSearchParams);
  };

  return (
    <>
      <AdminHeader title="Restaurant Referrals">
        <Div $flex $alignCenter $gap="20px" $gapSM="10px">
          <SearchInput
            value={search}
            maxWidth="300px"
            maxWidthSM="130px"
            placeholder="Search"
            onChange={onSearchChange}
          />
        </Div>
      </AdminHeader>

      <Div $m="32px 0 0" $mSM="20px 0 0">
        {list.totals && <ReferralSummaryCards totals={list.totals} />}
      </Div>

      <TabsWrapper>
        {REFERRAL_TABS.map((tab) => (
          <Tab
            key={tab.label}
            selected={status === tab.value}
            onClick={() =>
              handleSetUrlSearchParams(
                'status',
                tab.value ?? '',
                setSearchParams
              )
            }
          >
            {tab.label}
          </Tab>
        ))}
      </TabsWrapper>

      {list.isLoading ? (
        <AdminSpinner />
      ) : (
        <Div $relative>
          {list.isFetching && <AdminSpinner small absolute creme />}
          <TableHeader columns={REFERRAL_COLUMNS} />
          {list.referrals.map((referral) => (
            <ReferralRow
              key={referral.id}
              referral={referral}
              onOpen={(id) =>
                navigate(
                  generatePath(ADMIN_REFERRAL_DETAILS, { id: String(id) })
                )
              }
            />
          ))}
          {list.referrals.length === 0 && (
            <NoDataComponent
              height="30vh"
              heightSM="30vh"
              title={query ? 'No matches' : 'No referrals'}
              description={
                query
                  ? 'No restaurant or owner with that name here.'
                  : 'No restaurant has been referred by another one yet.'
              }
            />
          )}
        </Div>
      )}

      <ListSpinner
        loading={list.isFetchingNextPage}
        observerRef={sentinelRef}
      />
    </>
  );
};
